import { considerationToDimension } from '../../../constants/Constants'
import {
  StoreData,
  getDimensionName,
  getConsiderationName,
} from '../getPageContext'

interface IndicatorEntry {
  name: string
  quarters: { quarter: string; value: number; isCurrentQuarter?: boolean }[]
}

interface QuarterWeight {
  year: number
  quarter: number
  weights: { i: number; c: number; w: number }[]
  indicators: { id: number; value: number }[]
}

const buildUserAppliedFilters = (
  filters: {
    selectedDimension: number
    selectedConsideration: number
    searchKey: string
    isShowingPrevQuarters: boolean
  },
  locale?: string
): Record<string, unknown> => {
  const userAppliedFilters: Record<string, unknown> = {}
  if (filters.selectedDimension !== 0) {
    userAppliedFilters.dimension = getDimensionName(
      filters.selectedDimension,
      undefined,
      locale
    )
  }
  if (filters.selectedConsideration !== 0) {
    userAppliedFilters.consideration = getConsiderationName(
      filters.selectedConsideration,
      undefined,
      locale
    )
  }
  if (filters.searchKey && filters.searchKey.length >= 3) {
    userAppliedFilters.searchText = filters.searchKey
  }
  userAppliedFilters.showPreviousQuarters = filters.isShowingPrevQuarters
  return userAppliedFilters
}

const buildIndicatorMap = (
  quarterWeights: QuarterWeight[],
  indicatorNames: Record<number, { name: string }>,
  selectedQuarter: string | null
): Record<number, IndicatorEntry> => {
  const indicatorMap: Record<number, IndicatorEntry> = {}

  for (const q of quarterWeights) {
    const quarterLabel = `Q${q.quarter} ${q.year}`
    const qString = `${q.year}-${q.quarter}`
    const isCurrentQuarter =
      selectedQuarter === qString ||
      selectedQuarter === `${q.year}-Q${q.quarter}`

    for (const ind of q.indicators) {
      const existing = indicatorMap[ind.id] as IndicatorEntry | undefined
      if (!existing) {
        const nameEntry = indicatorNames[ind.id] as { name: string } | undefined
        indicatorMap[ind.id] = {
          name: nameEntry?.name ?? `Indicator ${ind.id}`,
          quarters: [],
        }
      }
      const entry: {
        quarter: string
        value: number
        isCurrentQuarter?: boolean
      } = {
        quarter: quarterLabel,
        value: Math.round(ind.value * 100 * 10) / 10,
      }
      if (isCurrentQuarter) {
        entry.isCurrentQuarter = true
      }
      indicatorMap[ind.id].quarters.push(entry)
    }
  }

  return indicatorMap
}

const applyFilters = (
  indicators: IndicatorEntry[],
  indicatorMap: Record<number, IndicatorEntry>,
  context: {
    quarterWeights: QuarterWeight[]
    filters: {
      selectedDimension: number
      selectedConsideration: number
      searchKey: string
      isShowingPrevQuarters: boolean
    }
  }
): IndicatorEntry[] => {
  const { quarterWeights, filters } = context
  let result = indicators

  // Filter by dimension
  if (filters.selectedDimension !== 0) {
    const allowedConsiderations = new Set<number>()
    for (const [cId, meta] of Object.entries(considerationToDimension)) {
      if (meta.dimension === filters.selectedDimension) {
        allowedConsiderations.add(Number(cId))
      }
    }
    const allowedIndicatorIds = new Set<number>()
    for (const q of quarterWeights) {
      for (const w of q.weights) {
        if (allowedConsiderations.has(w.c)) {
          allowedIndicatorIds.add(w.i)
        }
      }
    }
    result = result.filter((ind) => {
      const id = Object.entries(indicatorMap).find(([, v]) => v === ind)?.[0]
      return id && allowedIndicatorIds.has(Number(id))
    })
  }

  // Filter by consideration
  if (filters.selectedConsideration !== 0) {
    const allowedIndicatorIds = new Set<number>()
    for (const q of quarterWeights) {
      for (const w of q.weights) {
        if (w.c === filters.selectedConsideration) {
          allowedIndicatorIds.add(w.i)
        }
      }
    }
    result = result.filter((ind) => {
      const id = Object.entries(indicatorMap).find(([, v]) => v === ind)?.[0]
      return id && allowedIndicatorIds.has(Number(id))
    })
  }

  // Filter by search text
  if (filters.searchKey && filters.searchKey.length >= 3) {
    const search = filters.searchKey.toLowerCase()
    result = result.filter((ind) => ind.name.toLowerCase().includes(search))
  }

  // Filter out previous quarters if not showing them
  if (!filters.isShowingPrevQuarters) {
    result = result.map((ind) => ({
      ...ind,
      quarters: ind.quarters.filter((q) => q.isCurrentQuarter),
    }))
  }

  return result
}

export const buildIndicatorComparisonContent = (
  data?: StoreData
): Record<string, unknown> => {
  if (!data) return {}

  const { indicatorChartFilters, indicatorChartData, allInfo } = data

  // Build user applied filters
  const filters = indicatorChartFilters ?? {
    selectedDimension: 0,
    selectedConsideration: 0,
    searchKey: '',
    isShowingPrevQuarters: false,
  }

  const userAppliedFilters = buildUserAppliedFilters(filters, data.locale)

  // Build indicator data grouped by indicator
  if (!indicatorChartData || indicatorChartData.length === 0) {
    return { userAppliedFilters, indicators: [] }
  }

  // Build a map of indicator ID → name from allInfo
  const indicatorNames: Record<number, { name: string }> = {}
  if (allInfo?.LatestCityIndicators) {
    for (const ind of allInfo.LatestCityIndicators) {
      indicatorNames[ind.id] = { name: ind.name }
    }
  }

  // Build indicator weights lookup per quarter for filtering
  const quarterWeights: QuarterWeight[] = indicatorChartData.map((q) => ({
    year: q.year,
    quarter: q.quarter,
    weights: q.indicatorWeights ?? [],
    indicators: q.indicators ?? [],
  }))

  const indicatorMap = buildIndicatorMap(
    quarterWeights,
    indicatorNames,
    data.selectedQuarter
  )

  const indicators = applyFilters(Object.values(indicatorMap), indicatorMap, {
    quarterWeights,
    filters,
  })

  return { userAppliedFilters, indicators }
}
